import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { FileText, DollarSign, TrendingUp, Package, Download } from 'lucide-react';
import { useSession } from '@clerk/clerk-react';
import DetailedAnalyticsService from '../../../services/dashboard/detailedAnalyticsService';

const QuotationsAnalysis = ({ companyId }) => {
  const { session } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    if (session) {
      loadQuotationsData();
    }
  }, [companyId, dateRange, session]);

  const loadQuotationsData = async () => {
    if (!session) return;
    
    setLoading(true);
    try {
      const analyticsService = new DetailedAnalyticsService(session);
      const quotationsData = await analyticsService.getQuotationsData(companyId, dateRange);
      setData(quotationsData);
    } catch (error) {
      console.error('Error loading quotations data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pendiente': '#f59e0b',
      'aprobada': '#158B5C',
      'rechazada': '#ef4444',
      'vencida': '#6b7280',
      'en_revision': '#3b82f6'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      'pendiente': 'default',
      'aprobada': 'default',
      'rechazada': 'destructive',
      'vencida': 'secondary',
      'en_revision': 'default'
    };
    return variants[status] || 'secondary';
  };

  const filteredQuotations = selectedStatus === 'all' 
    ? data?.quotations || []
    : (data?.quotations || []).filter(quotation => quotation.estado === selectedStatus);

  const statusData = data?.summary?.statusCounts ? Object.entries(data.summary.statusCounts).map(([status, count]) => ({
    status,
    count,
    percentage: data.summary.totalQuotations > 0 ? (count / data.summary.totalQuotations) * 100 : 0
  })) : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Select value={dateRange.toString()} onValueChange={(value) => setDateRange(parseInt(value))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.keys(data?.summary?.statusCounts || {}).map(status => (
                <SelectItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Cotizaciones</p>
                <p className="text-2xl font-bold">{data?.summary?.totalQuotations || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Valor Total (UF)</p>
                <p className="text-2xl font-bold">{data?.summary?.totalValue?.toFixed(2) || '0.00'}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Valor Promedio (UF)</p>
                <p className="text-2xl font-bold">{data?.summary?.avgValue?.toFixed(2) || '0.00'}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Aprobadas</p>
                <p className="text-2xl font-bold">{data?.summary?.statusCounts?.aprobada || 0}</p>
              </div>
              <Package className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos y análisis */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Tendencias</TabsTrigger>
          <TabsTrigger value="status">Estados</TabsTrigger>
          <TabsTrigger value="details">Detalles</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tendencia de Cotizaciones</CardTitle>
              <CardDescription>
                Evolución diaria de cotizaciones y valores en los últimos {dateRange} días
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.chartData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Cantidad"
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="value" 
                    stroke="#158B5C" 
                    strokeWidth={2}
                    name="Valor (UF)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribución por Estado</CardTitle>
                <CardDescription>Cotizaciones por estado</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ status, count }) => `${status}: ${count}`}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estados - Resumen</CardTitle>
                <CardDescription>Cantidad y porcentaje por estado</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {statusData.map((item) => (
                    <div key={item.status} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: getStatusColor(item.status) }}
                        />
                        <span className="text-sm font-medium">
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1).replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{item.count}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Cotizaciones</CardTitle>
              <CardDescription>
                Lista detallada de cotizaciones {selectedStatus !== 'all' && `con estado ${selectedStatus}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Plan</th>
                      <th className="text-left p-2">Producto</th>
                      <th className="text-left p-2">Valor (UF)</th>
                      <th className="text-left p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuotations.slice(0, 50).map((quotation) => (
                      <tr key={quotation.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {new Date(quotation.created_at).toLocaleDateString('es-CL')}
                        </td>
                        <td className="p-2">
                          <span className="font-medium">{quotation.planName}</span>
                        </td>
                        <td className="p-2">
                          {quotation.productName}
                        </td>
                        <td className="p-2">
                          <span className="font-mono">{quotation.value?.toFixed(2) || '0.00'}</span>
                        </td>
                        <td className="p-2">
                          <Badge 
                            variant={getStatusBadgeVariant(quotation.estado)}
                            style={{ 
                              backgroundColor: getStatusColor(quotation.estado) + '20',
                              borderColor: getStatusColor(quotation.estado),
                              color: getStatusColor(quotation.estado)
                            }}
                          >
                            {quotation.estado?.charAt(0).toUpperCase() + quotation.estado?.slice(1).replace('_', ' ')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredQuotations.length > 50 && (
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Mostrando 50 de {filteredQuotations.length} cotizaciones
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QuotationsAnalysis;